import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Box
} from '@mui/material';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import { verifyEmailOTP, sendEmailOTP } from '../../api/authApi';

const EmailVerifyDialog = ({
  open,
  onClose,
  email,
  fullname,
  purpose = 'registration',
  onVerifySuccess,
  onVerify,
  onResend,
  title = 'Verify Your Email',
  description = 'Please enter the 6-digit verification code sent to your email.'
}) => {
  const [otp, setOtp] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(600); // 10 minutes in seconds
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);

  // Timer countdown
  useEffect(() => {
    if (open && timeRemaining > 0) {
      const timer = setTimeout(() => {
        setTimeRemaining(prev => prev - 1);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [open, timeRemaining]);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setOtp('');
      setError(null);
      setSuccess(null);
      setTimeRemaining(600);
    }
  }, [open]);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle OTP input change
  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only digits
    if (value.length <= 6) {
      setOtp(value);
      setError(null);
    }
  };

  // Handle verify OTP
  const handleVerify = async () => {
    if (otp.length !== 6) {
      setError('Please enter a 6-digit OTP');
      return;
    }

    try {
      setVerifying(true);
      setError(null);

      // Use custom onVerify if provided, otherwise use default verifyEmailOTP
      if (onVerify) {
        await onVerify(otp);
      } else {
        await verifyEmailOTP({ email, otp });
      }

      setSuccess('Email verified successfully!');

      // Call success callback after brief delay (only if provided)
      if (onVerifySuccess) {
        setTimeout(() => {
          onVerifySuccess();
        }, 1000);
      }
    } catch (err) {
      setError(err.message || 'Invalid OTP. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  // Handle resend OTP
  const handleResendOtp = async () => {
    try {
      setResending(true);
      setError(null);
      setSuccess(null);

      // Use custom onResend if provided, otherwise use default sendEmailOTP
      if (onResend) {
        await onResend();
      } else {
        await sendEmailOTP({
          email,
          fullname,
          purpose
        });
      }

      setTimeRemaining(600); // Reset timer
      setOtp(''); // Clear OTP field
      setSuccess('OTP resent successfully! Please check your email.');

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      setError(err.message || 'Failed to resend OTP. Please try again.');
    } finally {
      setResending(false);
    }
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && otp.length === 6 && !verifying) {
      handleVerify();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: '24px', overflow: 'hidden' } }}
    >
      <Box sx={{ background: 'linear-gradient(155deg, #4db2fc 0%, #1565C0 100%)', pt: 3.5, pb: 3, px: 3, color: '#fff', textAlign: 'center' }}>
        <Box sx={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
          <MarkEmailReadOutlinedIcon sx={{ fontSize: 26 }} />
        </Box>
        <Typography sx={{ fontSize: '18px', fontWeight: 800, fontFamily: 'Roboto, sans-serif' }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: '12.5px', opacity: 0.9, fontFamily: 'Roboto, sans-serif', mt: '4px', px: 1 }}>
          {description}
        </Typography>
      </Box>

      <DialogContent sx={{ pt: '24px !important', px: 3 }}>
        <Box sx={{ background: '#f9fafb', borderRadius: '14px', px: 2, py: 1.25, mb: 2.5, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '13px', color: '#667085', fontFamily: 'Roboto, sans-serif' }}>
            Code sent to <strong style={{ color: '#101828' }}>{email}</strong>
          </Typography>
        </Box>

        <TextField
          autoFocus
          fullWidth
          label="Enter 6-digit OTP"
          value={otp}
          onChange={handleOtpChange}
          onKeyPress={handleKeyPress}
          placeholder="000000"
          inputProps={{
            maxLength: 6,
            style: {
              fontSize: '24px',
              fontWeight: 'bold',
              textAlign: 'center',
              letterSpacing: '8px'
            }
          }}
          InputProps={{ sx: { borderRadius: '14px', backgroundColor: '#f9fafb' } }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#1565C0',
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: '#1565C0',
            },
          }}
          disabled={verifying || success}
        />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Time remaining: <strong>{formatTime(timeRemaining)}</strong>
          </Typography>
          <Button
            size="small"
            onClick={handleResendOtp}
            disabled={timeRemaining > 0 || resending || verifying || success}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: '#1565C0',
              '&:hover': {
                backgroundColor: 'rgba(21,101,192,0.08)'
              }
            }}
          >
            {resending ? <CircularProgress size={16} /> : 'Resend OTP'}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: '12px' }}>
            {success}
          </Alert>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          ⚠️ Never share this OTP with anyone. ARCDX will never ask for your OTP.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5 }}>
        <Button
          onClick={onClose}
          disabled={verifying}
          fullWidth
          sx={{ textTransform: 'none', fontWeight: 700, fontSize: '14px', color: '#667085', background: '#f2f4f7', borderRadius: '14px', py: 1.2, '&:hover': { background: '#e4e7ec' } }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleVerify}
          disabled={otp.length !== 6 || verifying || success}
          fullWidth
          sx={{
            textTransform: 'none',
            fontWeight: 800,
            fontSize: '14px',
            color: '#fff',
            background: 'linear-gradient(135deg,#4db2fc,#1565C0)',
            borderRadius: '14px',
            py: 1.2,
            boxShadow: '0 8px 20px rgba(21,101,192,0.35)',
            '&:hover': { background: 'linear-gradient(135deg,#39aafc,#0d47a1)' },
            '&.Mui-disabled': { background: '#e4e7ec', color: '#98a2b3' },
          }}
        >
          {verifying ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Verify'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailVerifyDialog;
